import type {
  AttributeField,
  AttributeSourceUser,
  BuildingHeaderAttributes,
} from "@/hooks/buildings/useBuildingAttributes";

export type FieldKey = "registry_code" | "net_area" | "floors";

/** Valid when empty (clears the value) or an integer — Registry code / Floors. */
export const isDigits = (v: string) => v.trim() === "" || /^\d+$/.test(v.trim());
/** Valid when empty or a decimal number (dot or comma separator) — Net area. */
export const isDecimal = (v: string) => v.trim() === "" || /^\d+([.,]\d+)?$/.test(v.trim());

const trimValue = (v: string) => v.trim();

/** `source` keeps its canonical English "Edited by:" / "Confirmed by:" marker
 * (the merge unit test depends on it); only the rendered label localizes. */
export const isConfirmedSource = (source?: string | null) =>
  source?.startsWith("Confirmed by:") === true;

export type SourceKind = "file" | "ehr" | "user" | "system";

/** Classify an attribute's `source` so the tooltip knows what to render. */
export function sourceKind(source?: string | null): SourceKind {
  if (!source) return "system";
  if (source === "EHR") return "ehr";
  if (source.startsWith("Edited by:") || isConfirmedSource(source)) return "user";
  if (source === "AI extraction") return "system";
  return "file";
}

/** Shape `UserAvatar` needs from an attribute's editor. */
export function toAvatarUser(user: AttributeSourceUser) {
  const [first, ...rest] = user.name.split(" ");
  return {
    id: user.id,
    email: user.name,
    first_name: first ?? null,
    last_name: rest.join(" ") || null,
    avatar_url: user.avatarUrl,
    color: user.color,
  };
}

export interface HeaderFieldSpec {
  key: FieldKey;
  label: string;
  /** Read-only text shown on the metadata row. */
  display: string;
  /** Pre-fills the edit popover. */
  initialValue: string;
  source: string | null;
  sourceUser: AttributeSourceUser | null;
  /** Gates the popover's Save button. */
  validate: (v: string) => boolean;
  /** Canonicalizes the typed value before it is persisted. */
  normalize: (v: string) => string;
  inputMode?: "numeric" | "decimal";
  /** Display-only unit rendered inside the input, e.g. "m²". */
  suffix?: string;
  widthClass?: string;
}

type ResolvedAttribute = {
  value: string;
  source: string | null;
  sourceUser: AttributeSourceUser | null;
};

function resolve(field: AttributeField | undefined): ResolvedAttribute {
  if (!field) return { value: "", source: null, sourceUser: null };
  return { value: field.value ?? "", source: field.source, sourceUser: field.sourceUser ?? null };
}

function resolveAll(attributes: BuildingHeaderAttributes | undefined) {
  return {
    netArea: resolve(attributes?.netArea),
    floors: resolve(attributes?.floors),
  };
}

interface HeaderFieldArgs {
  /** From `public.buildings.registry_code` (user-entered at creation, dual-written
   * on edit) — never from extractor findings / EHR / the user_data merge. */
  registryCode: string;
  attributes: BuildingHeaderAttributes | undefined;
  labels: { registryCode: string; netArea: string; floors: string };
}

/** The metadata row, as data: one spec per editable field. */
export function buildHeaderFields(args: HeaderFieldArgs): HeaderFieldSpec[] {
  const { registryCode, attributes, labels } = args;
  const { netArea, floors } = resolveAll(attributes);

  return [
    {
      key: "registry_code",
      label: labels.registryCode,
      display: registryCode || "—",
      initialValue: registryCode,
      source: null,
      sourceUser: null,
      validate: isDigits,
      normalize: trimValue,
      inputMode: "numeric",
    },
    {
      key: "net_area",
      label: labels.netArea,
      // Stored numeric values stay pure — the unit is appended at display
      // time only. See WHA-3137.
      display: netArea.value ? `${netArea.value} m²` : "—",
      initialValue: netArea.value,
      source: netArea.source,
      sourceUser: netArea.sourceUser,
      validate: isDecimal,
      // Accept an Estonian-style comma in the input but persist "123.5".
      normalize: (v) => v.trim().replace(",", "."),
      inputMode: "decimal",
      suffix: "m²",
    },
    {
      key: "floors",
      label: labels.floors,
      display: floors.value || "—",
      initialValue: floors.value,
      source: floors.source,
      sourceUser: floors.sourceUser,
      validate: isDigits,
      normalize: trimValue,
      inputMode: "numeric",
    },
  ];
}
